'use client';

import { useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { CommonCarousel, type CommonCarouselOptions } from '../../Carousel';
import { HomePageBrowseButton } from '../../HomePageBrowseButton/HomePageBrowseButton';
import { HomeGameCard } from '../HomeGameCard/HomeGameCard';

type HomeGamesCarouselGame = Readonly<{
  /** Keys into `<namespace>.<id>`. */
  id: string;
  backgroundClassName: string;
  foregroundClassName: string;
  href: string;
  imageSrc: string;
}>;

type HomeGamesCarouselProps = Readonly<{
  namespace: string;
  games: ReadonlyArray<HomeGamesCarouselGame>;
  ctaHref: string;
  headingId?: string;
  className?: string;
}>;

function HomeGamesCarousel({ namespace, games, ctaHref, headingId = 'home-games-carousel-heading', className }: HomeGamesCarouselProps) {
  const t = useTranslations(namespace);
  const isMobile = useIsMobile({ breakpoint: 'md' });

  const options: Partial<CommonCarouselOptions> = {
    fullWidth: true,
    showControls: false,
    disableHoverEffect: true,
    basis: 'w-75 max-w-[calc(100dvw-var(--ll-container-padding)*2)] lg:w-auto lg:shrink lg:grow lg:basis-0',
    controlsPosition: isMobile ? 'bottom' : 'top',
    btnMorePosition: isMobile ? 'center' : 'left',
  };

  return (
    <section aria-labelledby={headingId} data-testid="home-games-carousel" className={className}>
      <CommonCarousel
        title={
          <h2 id={headingId} className="heading-5">
            {t('title')}
          </h2>
        }
        subtitle={<p className="paragraph text-foreground-subtle">{t('subtitle')}</p>}
        btnMore={<HomePageBrowseButton href={ctaHref} text={t('cta')} isMobile={isMobile} data-testid="home-games-carousel-cta" data-track-name="Browse" />}
        shadcnOptions={{ align: 'start', containScroll: 'trimSnaps' }}
        options={options}
        data-track-carousel-title="Featured Lottos"
      >
        {games.map((game, index) => {
          const title = t(`${game.id}.title_line1`);
          const subtitle = t(`${game.id}.title_line2`);

          return (
            <HomeGameCard
              key={game.id}
              backgroundClassName={game.backgroundClassName}
              foregroundClassName={game.foregroundClassName}
              title={title}
              subtitle={subtitle}
              scheduleText={t(`${game.id}.schedule`)}
              href={game.href}
              imageSrc={game.imageSrc}
              testId={`home-games-carousel-${game.id}-card`}
              headingId={`home-games-carousel-${game.id}-heading`}
              data-track-container="Lottery"
              data-track-position={index + 1}
              data-track-product={title}
              data-track-value={subtitle}
            />
          );
        })}
      </CommonCarousel>
    </section>
  );
}

export { HomeGamesCarousel };
export type { HomeGamesCarouselGame, HomeGamesCarouselProps };
